import { Collection } from 'firestorter';

import BaseDocument from '../../Model/BaseDocument';
import Collections from '../../utils/collections';

const RecipeState = {
  NEW: 'NEW',
  EDIT: 'EDIT',
};

class Recipe extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.RECIPE}/${id}`, opts);
  }

  get image() {
    return this.data.image || '';
  }

  get totalCalories() {
    return this.data.totalCalories;
  }

  get name() {
    return this.data.name;
  }

  get protein() {
    return this.data.protein;
  }

  get carbs() {
    return this.data.carbs;
  }

  get fat() {
    return this.data.fat;
  }

  get proteinPercentage() {
    return this.data.proteinPercentage;
  }

  get carbsPercentage() {
    return this.data.carbsPercentage;
  }

  get fatPercentage() {
    return this.data.fatPercentage;
  }

  get ingredients() {
    return this.data.ingredients;
  }

  get instructions() {
    return this.data.instructions;
  }

  get preparationTime() {
    return this.data.preparationTime;
  }

  get cookingTime() {
    return this.data.cookingTime;
  }

  get allergenTags() {
    return this.data.allergenTags || [];
  }

  get tags() {
    return this.data.tags || [];
  }

  get servings() {
    return this.data.servings;
  }

  get public() {
    return typeof this.data.public === 'boolean' ? this.data.public : true;
  }

  get originalRecipe() {
    return this.data.originalRecipe;
  }

  get deleted() {
    return !!this.data.deleted;
  }

  get isArchived() {
    return !!this.data.isArchived;
  }

  get isScalingDisabled() {
    return this.data.isScalingDisabled || false;
  }

  get isAIGenerated() {
    return !!this.data.isAIGenerated;
  }

  async updateArchiveStatus(isArchived) {
    await this.updateFields({ isArchived: !!isArchived });
  }

  static async addDoc(data) {
    const recipeCollection = new Collection(Collections.RECIPE);
    const doc = await recipeCollection.add({
      isAIGenerated: false,
      ...data,
    });
    const recipeDoc = new Recipe(doc.id);
    await recipeDoc.init();
    return recipeDoc;
  }

  static async getAllRecipes() {
    const recipesCollection = new Collection(Collections.RECIPE, {
      createDocument: (src, opts) => new Recipe(src.id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref
        .where('isAIGenerated', '==', false)
        .orderBy('createdAt', 'desc'),
    });
    await recipesCollection.fetch();
    return recipesCollection;
  }

  // Gets all public recipes in the database
  static async getPublicRecipes() {
    const recipesCollection = new Collection(Collections.RECIPE, {
      createDocument: (src, opts) => new Recipe(src.id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref
        .where('public', '==', true)
        .orderBy('createdAt', 'desc'),
    });
    await recipesCollection.fetch();
    return recipesCollection;
  }

  // Gets all private recipes created by the user
  static async getRecipesByCreator(userId, fetchArchivedRecipes) {
    const recipesCollection = new Collection(Collections.RECIPE, {
      createDocument: (src, opts) => new Recipe(src.id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => {
        let query = ref
          .where('createdBy', '==', userId)
          .where('public', '==', false)
          .orderBy('createdAt', 'desc');

        if (!fetchArchivedRecipes) {
          query = query.where('isArchived', '==', false);
        }
        return query;
      },
    });
    await recipesCollection.fetch();
    return recipesCollection;
  }

  /**
   * Fetch recipes by document IDs (batched because Firestore `in` has a 10 item limit).
   * @param {Array<string>} recipeIds
   * @returns {Promise<Array<Recipe>>}
   */
  static async getRecipesByIds(recipeIds = []) {
    const ids = [...new Set(
      (Array.isArray(recipeIds) ? recipeIds : [])
        .filter((id) => typeof id === 'string' && id.trim().length > 0),
    )];

    if (ids.length === 0) {
      return [];
    }

    const fetchedRecipes = [];
    const promises = [];

    for (let i = 0; i < ids.length; i += 10) {
      const slice = ids.slice(i, i + 10);
      const recipesCollection = new Collection(Collections.RECIPE, {
        createDocument: (src, opts) => new Recipe(src.id, { ...opts, disableObserverRefCount: true }),
        // __name__ can be used to query by document id
        query: (ref) => ref.where('__name__', 'in', slice),
      });

      promises.push(Recipe.initCollection(recipesCollection).then(() => {
        fetchedRecipes.push(...recipesCollection.docs);
      }));
    }

    await Promise.all(promises);
    return fetchedRecipes;
  }
}

export default Recipe;
export { RecipeState };
